import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';

/**
 * The Policy Servicing agent's screens. A sender's name arrives in full; their
 * email address arrives shortened, and only in full in a request's detail.
 * Times are UTC ISO strings.
 */

export type PsStatus =
  | 'received' | 'understood' | 'policy_found' | 'calling' | 'call_ended'
  | 'asked_carrier' | 'carrier_replied' | 'answered'
  | 'reply_drafted' | 'replied' | 'needs_person' | 'discarded';

export interface PsRequestCard {
  id: string;
  received_at: string;
  sender: string;
  sender_email: string | null;
  subject: string | null;
  request_type: string | null;
  request_label: string | null;
  policy_num: string | null;
  carrier: string | null;
  status: PsStatus;
  stage: string;
  next_try_at: string | null;
  answer_summary: string | null;
  called: boolean;
  call_secs: number | null;
  call_attempts: number | null;
  reference_number: string | null;
  rep_name: string | null;
  needs_person_reason: string | null;
  reply_sent_at: string | null;
  completed_at: string | null;
  source: 'live' | 'seed';
  /** Set only for a request about an employer group's employee. */
  employer_id?: string | null;
  employer_name?: string | null;
  member_name?: string | null;
  /** "records" when it was answered from what we hold, "carrier" when a call or an email answered it. */
  answered_by?: 'records' | 'carrier' | null;
  /** Who it was handed to, by name, when the agent names someone. */
  assigned_to?: string | null;
  /** Handed to a person with a reply drafted for them to check and send. */
  draft_waiting?: boolean;
  /** The carrier was asked by email rather than called. */
  emailed_carrier?: boolean;
}

/** A change a call made to a record: what it was before, and what it is now. */
export interface PsRecordUpdate { record: string; field: string; before: string; after: string }

export interface PsRequestDetail extends PsRequestCard {
  email: { from_name: string | null; from_email: string | null; subject: string | null; body: string | null; received_at: string };
  question: string | null;
  /** For a group request: what our records held on the question, and why that did or did not answer it. */
  checked?: { label: string; value: string }[];
  checked_why?: string | null;
  /** What the call wrote back onto the employee's record. */
  updates?: PsRecordUpdate[];
  next_action?: string | null;
  facts: { label: string; value: string }[];
  outcome: {
    answer?: string | null; amount?: string | null; due_date?: string | null; requirements?: string | null;
    reference_number?: string | null; rep_name?: string | null; attempts?: number | null;
    /** Where the answer came from: a document, the policy record, or the carrier's email. */
    source?: string | null; confidence?: 'high' | 'medium' | 'low' | null;
  };
  /** A desk's own records, when it answered (or tried to) from them. */
  records?: {
    status: 'answered' | 'not_in_records' | 'needs_judgment';
    answer: string | null; source: string | null; confidence: string; why: string | null; checked: string[];
  } | null;
  /** The policy's documents the agent read, to open as PDFs. */
  documents?: { id: string; title: string }[];
  /** The email to the carrier, and its reply, when the carrier was emailed. */
  carrier_email?: { to: string; subject: string; body: string; sent_at: string } | null;
  carrier_reply?: { from: string; from_name: string | null; subject: string | null; body: string; received_at: string } | null;
  reply: { subject: string | null; body: string | null; sent_at: string | null };
  steps: { kind: string; at: string; title: string; detail: string | null }[];
  call: {
    status: string; duration_secs: number | null; summary: string | null;
    transcript: { role: 'agent' | 'user'; message: string }[];
  } | null;
}

export interface PsCall {
  id: string;
  started_at: string;
  carrier: string | null;
  request_label: string;
  policy_num: string | null;
  duration_secs: number | null;
  live: boolean;
  result: string;
  answer: string | null;
  reference_number: string | null;
}

export interface PsDay { day: string; value: number }

export interface PsSpotlight {
  flow: {
    mailbox: { address: string | null; provider: string | null; checked_at: string | null; error: string | null };
    today: { received: number; understood: number; policies_found: number; calls: number; replied: number };
  };
  tiles: {
    /** The small charts: the last 7 days, day by day. */
    received: PsDay[]; replied: PsDay[]; needs_person: PsDay[];
    /** The headline figures: everything the agent has handled so far. */
    totals: {
      answered: number;
      /** Of those, the ones answered from our own records, with no call. */
      answered_from_records?: number;
      carrier_calls: number;
      /** Requests where the carrier was asked by email rather than called. */
      carriers_emailed?: number;
      needs_person: number;
      in_progress: number;
      median_minutes_to_reply: number | null;
    };
    waiting_approval: number;
  };
  by_type: { key: string; label: string; value: number }[];
  handed_over: { label: string; value: number }[];
  /** How this agent works: how it asks carriers, and where its policies come from. */
  setup?: { carrier_channel: 'voice' | 'email'; answer_from_records: boolean; policy_system: string | null };
}

export type ReplyMode = 'auto' | 'approve' | 'auto_when_sure';

/** One agent's settings (backend services/policy_servicing/settings.py). */
export interface PsSettings {
  reply_mode: ReplyMode;
  agency_name: string | null;
  sign_off: string;
  line_of_business: 'life' | 'personal_lines';
  policy_source: 'warehouse' | 'connector';
  policy_connector_id: string | null;
  /** The policy system's name, when policies come from one: "Boston Software Single Point (sandbox)". */
  policy_system_name: string | null;
  answer_from_records: boolean;
  carrier_channel: 'voice' | 'email';
  carrier_reply_wait_minutes: number;
  escalation_owner_name: string | null;
  escalation_owner_email: string | null;
  escalation_alert: 'none' | 'email';
  draft_when_handed_over: boolean;
  policyholder_requests: boolean;
  employer_requests: boolean;
}

export interface PsConfiguration {
  reply_mode: ReplyMode;
  settings?: PsSettings;
  /** For an agent that emails carriers: the address it writes to at each one. */
  carrier_emails?: { name: string; email: string }[];
  agents: {
    name: string;
    voice: string | null;
    voice_style: string | null;
    status: 'live' | 'missing' | 'unknown';
    lines: { label: string; line: string }[];
  }[];
  agent_facts: { label: string; value: string }[];
  provider_error: string | null;
  request_types: {
    key: string; label: string; asks: string;
    /** Where its answer comes from, and where it goes when the records do not settle it. */
    answered_from?: 'records' | 'carrier' | 'person'; if_unanswered?: 'carrier' | 'person' | null;
  }[];
  /** For an employer's HR contact. Empty where the workspace has no employer accounts. */
  group_request_types?: { key: string; label: string; answered_from: string }[];
  /** The demonstration employer account, where the workspace has it. Null otherwise: no reset is offered. */
  demo_account?: {
    id: string; name: string; group_number: string; requests: number; changed_by_calls: number; on_a_call: boolean;
  } | null;
  voice_agent: { name: string; opening: string; longest_call_minutes: number; tries: number; retry_minutes: number };
  shared_with_carrier: string[];
  rules: { when: string; then: string; detail: string }[];
  finds_out: { key: string; label: string; detail: string }[];
  guardrails: { title: string; detail: string }[];
}

export interface PsIntegration {
  id: 'mailbox' | 'warehouse' | 'dialer' | 'policy_system' | 'carrier_email';
  name: string;
  role: string;
  connected: boolean;
  address?: string | null;
  checked_at?: string | null;
  handled_today?: number;
  error?: string | null;
  detail?: string;
  built_on?: string[];
  calls_today?: number;
}

/** An employer group the agency looks after, as a row in the Accounts list. */
export interface PsAccountCard {
  id: string;
  name: string;
  group_number: string;
  industry: string | null;
  location: string | null;
  employees: number;
  plans: string[];
  waiting_on_carrier: number;
  open_requests: number;
  renewal_on: string | null;
}

export interface PsPlan {
  id: string;
  line: string;
  carrier: string;
  name: string;
  plan_year: string | null;
  benefits: { label: string; value: string }[];
  rates: { tier: string; amount: number }[];
  enrolled: number;
}

export interface PsMember {
  id: string;
  name: string;
  job_title: string | null;
  employment: string;
  hired_on: string | null;
  /** Whether we hold one. The date itself is never sent to the screen. */
  date_of_birth: 'On file' | 'Missing';
  status: string;
  medical: string;
  dental: string;
}

export interface PsEnrollment {
  id: string;
  member: string | null;
  plan: string | null;
  carrier: string | null;
  kind: string;
  submitted_on: string | null;
  effective_on: string | null;
  internal_status: string;
  carrier_status: string;
  reason: string | null;
  next_action: string | null;
  reference: string | null;
  request_id: string | null;
}

export interface PsInvoiceLine {
  id: string;
  member: string | null;
  kind: 'premium' | 'credit';
  tier: string | null;
  amount: number;
  note: string | null;
  request_id: string | null;
}

export interface PsInvoice {
  id: string;
  carrier: string | null;
  plan: string | null;
  period: string;
  period_label: string;
  number: string | null;
  issued_on: string | null;
  due_on: string | null;
  status: string;
  total: number;
  members_billed: number;
  lines: PsInvoiceLine[];
}

export interface PsAccount {
  id: string;
  name: string;
  group_number: string;
  industry: string | null;
  location: string | null;
  renewal_on: string | null;
  contacts: { name: string | null; title: string | null; email: string | null }[];
  rules: { label: string; value: string; detail: string }[];
  counts: { employees: number; active: number; waiting_on_carrier: number; open_requests: number; requests: number };
  plans: PsPlan[];
  members: PsMember[];
  enrollments: PsEnrollment[];
  invoices: PsInvoice[];
  requests: PsRequestCard[];
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('klaris_jwt');
  return { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) };
}

function prefix(): string {
  const tid = getTenantIdFromStorage();
  if (!tid) throw new Error('Not authenticated');
  return `${tenantApiPrefix(tid)}/policy-servicing`;
}

/**
 * A path on one Policy Servicing agent. A workspace may have several (one per
 * service inbox), so every call names the agent it is about; the server
 * answers for the first when none is named.
 */
function agentPath(agentId: string, path: string, query: Record<string, string> = {}): string {
  return `${prefix()}${path}?${new URLSearchParams({ ...query, agent: agentId })}`;
}

async function call<T>(agentId: string, path: string, init: RequestInit, fallback: string,
  query: Record<string, string> = {}): Promise<T> {
  const response = await fetch(buildApiUrl(agentPath(agentId, path, query)), { ...init, headers: getAuthHeaders() });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: unknown }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : fallback);
  }
  return response.json();
}

/** A list of requests, and whether this agent calls carriers or emails them. */
export interface PsRequestList { requests: PsRequestCard[]; carrier_channel?: 'voice' | 'email' }

export const fetchLiveRequests = (agentId: string) =>
  call<PsRequestList>(agentId, '/requests', { method: 'GET' }, 'Could not load live requests', { scope: 'live' });
export const fetchCompletedRequests = (agentId: string) =>
  call<PsRequestList>(agentId, '/requests', { method: 'GET' }, 'Could not load completed requests', { scope: 'completed' });
export const fetchRequest = (agentId: string, id: string) =>
  call<PsRequestDetail>(agentId, `/requests/${id}`, { method: 'GET' }, 'Could not load that request');
export const sendReply = (agentId: string, id: string, body?: string) =>
  call<PsRequestDetail>(agentId, `/requests/${id}/send`, { method: 'POST', body: JSON.stringify({ body }) }, 'Could not send the reply');
/**
 * Take a request out of the live list: nothing is called about or replied to,
 * and its story says who took it out. Refused while the carrier is on the phone.
 */
export const discardRequest = (agentId: string, id: string) =>
  call<PsRequestDetail>(agentId, `/requests/${id}/discard`, { method: 'POST' }, 'Could not discard the request');
export const fetchAccounts = (agentId: string) =>
  call<{ accounts: PsAccountCard[] }>(agentId, '/accounts', { method: 'GET' }, 'Could not load accounts');
export const fetchAccount = (agentId: string, id: string) =>
  call<PsAccount>(agentId, `/accounts/${id}`, { method: 'GET' }, 'Could not load that account');

/** A plan's summary of benefits as a PDF, as an object URL. The caller revokes it. */
export async function planDocument(agentId: string, accountId: string, planId: string): Promise<string> {
  const response = await fetch(buildApiUrl(agentPath(agentId, `/accounts/${accountId}/plans/${planId}/document.pdf`)), {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Could not open the plan document');
  return URL.createObjectURL(await response.blob());
}

/** One of the agent's policy documents (a declarations page) as a PDF, as an object URL. The caller revokes it. */
export async function policyDocument(agentId: string, documentId: string): Promise<string> {
  const response = await fetch(buildApiUrl(agentPath(agentId, `/documents/${documentId}.pdf`)), {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Could not open the document');
  return URL.createObjectURL(await response.blob());
}

export const fetchPsCalls = (agentId: string) =>
  call<{ calls: PsCall[] }>(agentId, '/calls', { method: 'GET' }, 'Could not load conversations');
export const fetchPsSpotlight = (agentId: string) =>
  call<PsSpotlight>(agentId, '/spotlight', { method: 'GET' }, 'Could not load Spotlight');
export const fetchPsConfiguration = (agentId: string) =>
  call<PsConfiguration>(agentId, '/configuration', { method: 'GET' }, 'Could not load how the agent is set up');
export const setReplyMode = (agentId: string, reply_mode: ReplyMode) =>
  call<PsConfiguration>(agentId, '/settings', { method: 'PATCH', body: JSON.stringify({ reply_mode }) }, 'Could not change the setting');
/**
 * Put the demonstration employer account back to its starting state: its
 * employees as they were before any call, and the requests about them removed.
 * Refused while a carrier is on the phone about one of them.
 */
export const resetDemoData = (agentId: string) =>
  call<PsConfiguration>(agentId, '/demo/reset', { method: 'POST' }, 'Could not reset the demo data');
export const fetchPsIntegrations = (agentId: string) =>
  call<{ integrations: PsIntegration[] }>(agentId, '/integrations', { method: 'GET' }, 'Could not load integrations');

/** Listen for "the requests changed" until `signal` aborts. Carries no data. */
export async function watchRequests(onChange: () => void, signal: AbortSignal, onOpen?: () => void): Promise<void> {
  const response = await fetch(buildApiUrl(`${prefix()}/requests/stream`), {
    headers: { ...getAuthHeaders(), Accept: 'text/event-stream' }, signal,
  });
  if (!response.ok || !response.body) throw new Error(`Live updates unavailable (${response.status})`);
  onOpen?.();
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    let end = buffer.indexOf('\n\n');
    while (end !== -1) {
      if (buffer.slice(0, end).split('\n').includes('event: changed')) onChange();
      buffer = buffer.slice(end + 2);
      end = buffer.indexOf('\n\n');
    }
  }
}

export async function downloadPsList(agentId: string, list: 'live' | 'completed' | 'calls'): Promise<string> {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const response = await fetch(buildApiUrl(agentPath(agentId, '/export.xlsx', { list, tz })), {
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error('Could not download that list');
  const filename = response.headers.get('Content-Disposition')?.match(/filename=["']?([^"';]+)/)?.[1]
    ?? `policy-servicing-${list}.xlsx`;
  const link = document.createElement('a');
  link.href = URL.createObjectURL(await response.blob());
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
  return filename;
}
